import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';
import { ShieldAlert, FileCheck, Clock, Lock } from 'lucide-react';
import { toast } from 'sonner';
import {
  PageHeader,
  LoadingState,
  MetricCard,
  KPIGrid,
  StatusBadge,
  DataTable,
  Column,
  SearchInput,
} from '@/components/common';
import { Card, CardContent, CardHeader, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { permitsApi, type PermitCase, type HeldRequest } from '@/api/services';
import { getErrorMessage } from '@/api/client';
import { formatDate } from '@/lib/utils';

/** Days until a date, negative once past. */
const daysUntil = (iso: string | null) =>
  iso === null ? null : Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);

export default function Permits() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const jobId = searchParams.get('jobId') ?? undefined;
  const [cases, setCases] = useState<PermitCase[]>([]);
  const [held, setHeld] = useState<HeldRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [caseRes, heldRes] = await Promise.all([
        permitsApi.getCases({ search: search || undefined, jobId, limit: 50 }),
        permitsApi.getHeldRequests(),
      ]);
      setCases(caseRes.data);
      setHeld(heldRes);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [jobId, search]);

  useEffect(() => {
    load();
  }, [search, load]);

  const expiringSoon = cases.filter((c) => {
    const d = daysUntil(c.permitExpiryDate);
    return d !== null && d >= 0 && d <= 30;
  }).length;

  const columns: Column<PermitCase>[] = [
    {
      key: 'caseNumber',
      header: 'Case',
      sortable: true,
      render: (row) => (
        <div>
          <p className="font-medium">{row.caseNumber}</p>
          <p className="text-xs text-muted-foreground">{row.commercialInvoiceRef}</p>
        </div>
      ),
    },
    {
      key: 'caseType',
      header: 'Type',
      sortable: true,
      render: (row) => <span className="text-sm">{row.caseType.replace(/_/g, ' ')}</span>,
    },
    {
      key: 'country',
      header: 'Country',
      sortable: true,
      accessorFn: (row) => row.countryRegime?.countryName ?? '',
      render: (row) => <span className="text-sm">{row.countryRegime?.countryName ?? '—'}</span>,
    },
    {
      key: 'permitReference',
      header: 'Permit',
      sortable: true,
      render: (row) =>
        row.permitReference ? (
          <span className="font-mono text-xs">{row.permitReference}</span>
        ) : (
          <span className="text-xs text-muted-foreground">not yet issued</span>
        ),
    },
    {
      key: 'permitExpiryDate',
      header: 'Expires',
      sortable: true,
      render: (row) => {
        const d = daysUntil(row.permitExpiryDate);
        if (d === null) return <span className="text-muted-foreground">—</span>;
        return (
          <div>
            <p className="text-sm">{formatDate(row.permitExpiryDate!)}</p>
            <p
              className={
                d < 0
                  ? 'text-xs font-medium text-status-destructive-foreground'
                  : d <= 30
                    ? 'text-xs font-medium text-status-warning-foreground'
                    : 'text-xs text-muted-foreground'
              }
            >
              {d < 0 ? `${Math.abs(d)}d overdue` : `${d}d remaining`}
            </p>
          </div>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (row) => <StatusBadge status={row.status} />,
    },
  ];

  if (loading && cases.length === 0) return <LoadingState />;

  return (
    <motion.div variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          title="Temporary Permits"
          description="Import and export permit cases by country, with expiry dates and the movements held against them."
        />
      </motion.div>

      <div className="mt-6">
        <KPIGrid columns={4}>
          <MetricCard
            label="Requests on Hold"
            value={held.length}
            sentiment={held.length > 0 ? 'warning' : 'neutral'}
            subtitle="cannot raise an RFQ"
            icon={<Lock className="h-5 w-5" />}
          />
          <MetricCard
            label="Open Cases"
            value={cases.filter((c) => !['CLOSED', 'CANCELLED'].includes(c.status)).length}
            sentiment="info"
            icon={<FileCheck className="h-5 w-5" />}
          />
          <MetricCard
            label="Expiring in 30 Days"
            value={expiringSoon}
            sentiment={expiringSoon > 0 ? 'warning' : 'success'}
            icon={<Clock className="h-5 w-5" />}
          />
          <MetricCard
            label="Permits Issued"
            value={cases.filter((c) => c.permitReference).length}
            sentiment="success"
            icon={<ShieldAlert className="h-5 w-5" />}
          />
        </KPIGrid>
      </div>

      {/* The queue the module exists to surface: movements that cannot ship. */}
      {held.length > 0 && (
        <motion.div variants={fadeUp} className="mt-6">
          <Card>
            <CardHeader>
              <h3 className="text-base font-semibold text-foreground">Held for Compliance</h3>
              <CardDescription>
                {held.length} movement{held.length === 1 ? '' : 's'} blocked until a permit is in
                hand. No RFQ can be raised against these.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {held.map((h) => (
                  <div
                    key={h.id}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-muted p-3"
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-foreground">{h.transportOrder.orderNumber}</p>
                      <p className="text-xs text-muted-foreground">
                        {h.transportOrder.originCountry} → {h.transportOrder.destCountry} ·{' '}
                        {h.movementType.replace(/_/g, ' ').toLowerCase()}
                        {h.reason ? ` · ${h.reason}` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {h.permitCases.length === 0 ? (
                        <Badge variant="outline">no permit case yet</Badge>
                      ) : (
                        h.permitCases.map((c) => (
                          <button
                            key={c.id}
                            onClick={() => navigate(`/permits/${c.id}`)}
                            className="rounded-md border border-border bg-card px-2 py-1 text-xs font-medium transition-colors hover:bg-accent"
                          >
                            {c.caseNumber}
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      <motion.div variants={fadeUp} className="mt-6">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search by case, permit reference, invoice, asset or serial..."
          className="max-w-md"
        />
      </motion.div>

      <motion.div variants={fadeUp} className="mt-4">
        <DataTable
          pagination
          columns={columns}
          data={cases}
          onRowClick={(row) => navigate(`/permits/${row.id}`)}
          emptyTitle="No permit cases"
          emptyDescription="Cases appear here once a movement is classified as temporary or licence-required."
        />
      </motion.div>
    </motion.div>
  );
}
